// @flow
import api from '../api/item';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_ITEM_SHIPPING_FAIL = 'la/domain/itemShipping/LOAD_FAIL';
export const LOAD_ITEM_SHIPPING_REQUEST = 'la/domain/itemShipping/LOAD_REQUEST';
export const LOAD_ITEM_SHIPPING_SUCCESS = 'la/domain/itemShipping/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_ITEM_SHIPPING_FAIL:
            return {
                ...state,
                loading: difference(state.loading, action.meta.itemIds),
            };
        case LOAD_ITEM_SHIPPING_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_ITEM_SHIPPING_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = cloneDeep(state.loaded);
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (action.payload.itemShippings) {
                action.payload.itemShippings.forEach(x => {
                    existing[x.itemId] = { ...x };
                    loaded[x.itemId] = time;
                    loading = difference(loading, [x.itemId]);
                });
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.itemShipping;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

// takes in a (state, id) and passes both params to every function on
// the way down.
export const getItemShipping = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || {});

export const getLoadTimeForItemShipping = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

export const isItemShippingLoading = createSelector([loadingSelector, idSelector], (loading, id) =>
    loading.includes(id)
);

const shouldFetchItemShipping = (state, itemId) => {
    if (!itemId) return false;
    const item = getItemShipping(state, itemId);
    if (item) {
        const loaded = getLoadTimeForItemShipping(state, itemId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) return false;
    }
    const loading = isItemShippingLoading(state, itemId);
    return !loading;
};

const whichItemShippingsNeeded = (state, itemIds) => itemIds.filter(itemId => shouldFetchItemShipping(state, itemId));

/* ACTION CREATORS */
const loadItemShippings = itemIds => async (dispatch, getState) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);

        dispatch({
            payload: itemIds,
            type: LOAD_ITEM_SHIPPING_REQUEST,
        });
        const response = await api.fetchItemShippingsByIds({ deployment, itemIds });
        dispatch({
            meta: { actionTime: Date.now(), itemIds },
            payload: response.data,
            type: LOAD_ITEM_SHIPPING_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { itemIds },
            payload: error,
            type: LOAD_ITEM_SHIPPING_FAIL,
        });
    }
};

export const fetchItemShippingsIfNeeded = (itemIds: number[]) => async (dispatch: Function, getState: Function) => {
    const needed = whichItemShippingsNeeded(getState(), itemIds);
    if (needed.length) {
        return dispatch(loadItemShippings(needed));
    }
    return Promise.resolve();
};
